// =============================================
// SERVICE: API de Ocorrências de Divergência
// =============================================

import { apiService } from './api';
import { buildApiUrl } from './config';

function buildUrlWithParams(path: string, params?: Record<string, unknown>): string {
  const url = buildApiUrl(path);
  if (!params) return url;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

export interface DivergenciaOcorrenciaEtiqueta {
  id: number;
  divergencia_ocorrencia_id: number;
  empresa_id: number;
  codigo_lido: string;
  volume_atual: number | null;
  total_volumes: number | null;
  lido_em: string | null;
  status: string;
  criado_em: string;
}

export interface DivergenciaOcorrencia {
  id: number;
  empresa_id: number;
  conferencia_id: number;
  cliente_id: number | null;
  numero_pedido: string;
  ocorrencia_tipo: string;
  justificativa: string | null;
  total_volumes_esperado: number;
  total_volumes_conferido: number;
  total_volumes_faltante: number;
  volumes_conferidos: number[];
  volumes_faltantes: number[];
  is_ponto_corte: boolean;
  status: 'pendente' | 'analisando' | 'resolvido';
  registrado_por: number | null;
  novo_registrado_por: number | null;
  registrado_por_nome: string | null;
  resolvido_por: number | null;
  novo_resolvido_por: number | null;
  resolvido_por_nome: string | null;
  resolvido_em: string | null;
  observacoes: string | null;
  cliente_razao_social: string | null;
  etiquetas: DivergenciaOcorrenciaEtiqueta[];
  criado_em: string;
  atualizado_em: string;
}

export interface DivergenciaOcorrenciaCreateData {
  conferencia_id: number;
  cliente_id?: number;
  numero_pedido: string;
  ocorrencia_tipo?: string;
  justificativa?: string;
  total_volumes_esperado?: number;
  total_volumes_conferido?: number;
  total_volumes_faltante?: number;
  volumes_conferidos?: number[];
  volumes_faltantes?: number[];
  etiquetas?: {
    codigo_lido: string;
    volume_atual?: number;
    total_volumes?: number;
    lido_em?: string;
    status?: string;
  }[];
}

export interface DivergenciaOcorrenciaEstatisticasPorTipo {
  tipo: string;
  total: number;
  pendentes: number;
  analisando: number;
  resolvidos: number;
}

export interface DivergenciaOcorrenciaEstatisticas {
  total: number;
  pendentes: number;
  resolvidos: number;
  analisando: number;
  total_volumes_faltantes: number;
  por_tipo: DivergenciaOcorrenciaEstatisticasPorTipo[];
}

export async function listarDivergenciaOcorrencias(params?: {
  page?: number;
  limit?: number;
  status?: string;
  numero_pedido?: string;
  cliente_id?: number | string;
  conferencia_id?: number | string;
  busca?: string;
  data_inicio?: string;
  data_fim?: string;
}): Promise<{ success: boolean; ocorrencias: DivergenciaOcorrencia[]; pagination: any }> {
  const url = buildUrlWithParams('/divergencia-ocorrencias', params as Record<string, unknown>);
  return apiService.request(url);
}

export async function buscarDivergenciaOcorrencia(id: number): Promise<{ success: boolean; data: DivergenciaOcorrencia }> {
  const url = buildApiUrl(`/divergencia-ocorrencias/${id}`);
  return apiService.request(url);
}

export async function buscarPorConferencia(conferenciaId: number): Promise<{ success: boolean; ocorrencias: DivergenciaOcorrencia[] }> {
  const url = buildApiUrl(`/divergencia-ocorrencias/conferencia/${conferenciaId}`);
  return apiService.request(url);
}

export async function criarDivergenciaOcorrencia(data: DivergenciaOcorrenciaCreateData): Promise<{ success: boolean; data: DivergenciaOcorrencia; message: string }> {
  const url = buildApiUrl('/divergencia-ocorrencias');
  return apiService.request(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function criarDivergenciaOcorrenciaBatch(ocorrencias: DivergenciaOcorrenciaCreateData[]): Promise<{ success: boolean; data: DivergenciaOcorrencia[]; total: number; tem_ponto_corte?: boolean; message: string }> {
  const url = buildApiUrl('/divergencia-ocorrencias/batch');
  return apiService.request(url, {
    method: 'POST',
    body: JSON.stringify({ ocorrencias })
  });
}

export async function atualizarDivergenciaOcorrencia(id: number, data: {
  status?: string;
  justificativa?: string;
  observacoes?: string;
  ocorrencia_tipo?: string;
}): Promise<{ success: boolean; data: DivergenciaOcorrencia; message: string }> {
  const url = buildApiUrl(`/divergencia-ocorrencias/${id}`);
  return apiService.request(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarDivergenciaOcorrencia(id: number): Promise<{ success: boolean; message: string }> {
  const url = buildApiUrl(`/divergencia-ocorrencias/${id}`);
  return apiService.request(url, {
    method: 'DELETE'
  });
}

export async function obterEstatisticasDivergenciaOcorrencias(params?: {
  data_inicio?: string;
  data_fim?: string;
}): Promise<{ success: boolean; data: DivergenciaOcorrenciaEstatisticas }> {
  const url = buildUrlWithParams('/divergencia-ocorrencias/estatisticas', params as Record<string, unknown>);
  return apiService.request(url);
}
